const handlePDFDownload = async (
  setSnackbarData,
  theme,
  isMobile,
  calcType
) => {
  try {
    // Create progress indicator
    const progressContainer = document.createElement("div");
    Object.assign(progressContainer.style, {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      backgroundColor: theme.palette.background.paper,
      padding: "20px 30px",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      zIndex: 9999,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "15px",
    });

    const progressText = document.createElement("span");
    progressText.style.color = theme.palette.text.primary;
    progressText.style.fontSize = "16px";
    progressText.style.fontWeight = "500";
    progressText.textContent = "Generating PDF... 0%";

    const progressBar = document.createElement("div");
    Object.assign(progressBar.style, {
      width: "200px",
      height: "6px",
      backgroundColor: theme.palette.grey[200],
      borderRadius: "3px",
    });

    const progressFill = document.createElement("div");
    Object.assign(progressFill.style, {
      width: "0%",
      height: "100%",
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
      transition: "width 0.3s",
    });

    progressBar.appendChild(progressFill);
    progressContainer.appendChild(progressText);
    progressContainer.appendChild(progressBar);
    document.body.appendChild(progressContainer);

    // Update progress function
    const updateProgress = (percent) => {
      progressText.textContent = `Generating PDF... ${percent}%`;
      progressFill.style.width = `${percent}%`;
    };

    // Import required libraries
    const jsPDFModule = await import("jspdf");
    const html2canvasModule = await import("html2canvas");
    const jsPDF = jsPDFModule.default;
    const html2canvas = html2canvasModule.default;

    // Get main container
    const container = document.getElementById("calculator-to-print");
    if (!container) throw new Error("Calculator content not found");

    // Wait for any dynamic content to render
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Find all sections marked for PDF
    const pdfSections = Array.from(
      container.querySelectorAll("[id^='pdf-']")
    ).filter((section) => {
      // Only include sections that have visible content
      const rect = section.getBoundingClientRect();

      return rect.width > 0 && rect.height > 0;
    });

    if (!pdfSections.length) throw new Error("No visible PDF sections found");

    // Initialize PDF
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - 2 * margin;
    let currentY = 25;

    // Add header to current page
    const addHeaderToPage = () => {
      // Use theme primary color
      const primaryColor = theme.palette.primary.main;
      // Convert hex to RGB
      const r = parseInt(primaryColor.slice(1, 3), 16);
      const g = parseInt(primaryColor.slice(3, 5), 16);
      const b = parseInt(primaryColor.slice(5, 7), 16);

      doc.setFillColor(r, g, b);
      doc.rect(0, 0, pageWidth, 20, "F");

      // White text
      doc.setTextColor(255, 255, 255);
      doc.setFont("Roboto", "normal");
      doc.setFontSize(20);

      // Add logo text
      doc.text("OfferMarket", margin, 14);

      // Add calculator name with same size
      doc.text(`${calcType} calculator`, margin + 55, 14);

      // Reset text color to black for content
      doc.setTextColor(0, 0, 0);
      doc.setFont("Helvetica", "normal");

      return 25;
    };

    // Update footer to match brand
    const addFooterToPage = (pageNum, totalPages) => {
      const dateStr = new Date().toLocaleDateString();
      doc.setFont("Roboto", "normal");
      doc.setFontSize(11);

      // Use theme primary color for footer text
      const primaryColor = theme.palette.primary.main;
      const r = parseInt(primaryColor.slice(1, 3), 16);
      const g = parseInt(primaryColor.slice(3, 5), 16);
      const b = parseInt(primaryColor.slice(5, 7), 16);
      doc.setTextColor(r, g, b);

      doc.text(
        `Generated on ${dateStr} via OfferMarket.us | Page ${pageNum} of ${totalPages}`,
        margin,
        pageHeight - 12
      );

      // Reset font and color
      doc.setFont("Helvetica", "normal");
      doc.setTextColor(0, 0, 0);
    };

    let currentPage = 1;
    currentY = addHeaderToPage();

    // Process each section
    for (let i = 0; i < pdfSections.length; i++) {
      const section = pdfSections[i];
      const progress = Math.round((i / pdfSections.length) * 100);
      updateProgress(progress);

      // Hide elements that shouldn't be in PDF
      const noPrintElements = section.querySelectorAll(".no-print-element");
      noPrintElements.forEach((el) => (el.style.display = "none"));

      try {
        // Check for special content types
        // NOTE IMPORTANT
        const hasChart = section.querySelector(".tv-lightweight-charts");
        const hasTables = section.querySelectorAll("table");
        // Check if this section contains user inputs
        const isUserInput = section.id && section.id.includes("user-input");

        if (hasChart) {
          // Handle chart sections

          // Ensure chart is fully rendered
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Get chart container and force full width
          const chartContainer = hasChart.parentElement;
          const originalStyles = {
            width: chartContainer.style.width,
            height: chartContainer.style.height,
          };

          // Set chart width to match PDF page width
          const pdfWidth = contentWidth * 3.78; // Convert mm to px
          chartContainer.style.width = "100%";
          chartContainer.style.maxWidth = "100%";

          // Force chart resize
          const chart = hasChart.__chartInstance;
          if (chart && chart.applyOptions) {
            chart.applyOptions({
              width: pdfWidth,
              height: pdfWidth * 0.5, // 2:1 aspect ratio
              layout: {
                background: { type: "solid", color: "#FFFFFF" },
              },
            });
          }

          // Wait for resize
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Capture with full width
          const canvas = await html2canvas(section, {
            scale: 1,
            useCORS: true,
            logging: true,
            width: pdfWidth,
            windowWidth: pdfWidth,
          });

          // Restore original styles
          Object.assign(chartContainer.style, originalStyles);

          // Add to PDF
          const imgWidth = contentWidth;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          if (currentY + imgHeight > pageHeight - 20) {
            doc.addPage();
            currentPage++;
            currentY = addHeaderToPage();
          }

          doc.addImage(canvas, "PNG", margin, currentY, imgWidth, imgHeight);
          currentY += imgHeight + 10;
        } else if (hasTables.length) {
          // Handle table sections

          for (const table of hasTables) {
            // Store original table HTML
            const originalHtml = table.outerHTML;

            // Get all rows
            const rows = Array.from(table.querySelectorAll("tr"));
            const headerRow = rows[0];
            const dataRows = rows.slice(1);

            // Calculate rows per page (approximate)
            const rowHeight = 25; // Approximate height per row in mm
            const availableHeight = pageHeight - 60; // Page height minus margins and headers
            const rowsPerPage = Math.floor(availableHeight / rowHeight);

            // Process rows in chunks
            for (let j = 0; j < dataRows.length; j += rowsPerPage) {
              // Create a wrapper div for this chunk
              const wrapper = document.createElement("div");
              wrapper.style.width = "100%";

              // Create new table for this chunk
              const chunkTable = document.createElement("table");
              chunkTable.className = table.className;
              chunkTable.style.width = "100%";

              // Copy header
              const thead = document.createElement("thead");
              thead.innerHTML = headerRow.outerHTML;
              chunkTable.appendChild(thead);

              // Create tbody and add rows for this chunk
              const tbody = document.createElement("tbody");
              const endIndex = Math.min(j + rowsPerPage, dataRows.length);

              for (let k = j; k < endIndex; k++) {
                tbody.appendChild(dataRows[k].cloneNode(true));
              }

              chunkTable.appendChild(tbody);
              wrapper.appendChild(chunkTable);

              // Temporarily add wrapper to document to capture
              table.parentNode.insertBefore(wrapper, table);

              // Capture this chunk
              const canvas = await html2canvas(wrapper, {
                scale: 2,
                useCORS: true,
                logging: true,
                windowWidth: contentWidth * 3.78,
              });

              // Remove temporary wrapper
              wrapper.remove();

              // Add to PDF
              const imgWidth = contentWidth;
              const imgHeight = (canvas.height * imgWidth) / canvas.width;

              if (currentY + imgHeight > pageHeight - 20) {
                doc.addPage();
                currentPage++;
                currentY = addHeaderToPage();
              }

              doc.addImage(
                canvas,
                "PNG",
                margin,
                currentY,
                imgWidth,
                imgHeight
              );

              // Force new page after each chunk (except last)
              if (endIndex < dataRows.length) {
                doc.addPage();
                currentPage++;
                currentY = addHeaderToPage();
              } else {
                currentY += imgHeight + 10;
              }
            }

            // Restore original table
            table.outerHTML = originalHtml;
          }
        } else if (isUserInput) {
          // Store original styles
          const elements = section.querySelectorAll("*");
          const originalStyles = Array.from(elements).map((el) => ({
            element: el,
            fontSize: el.style.fontSize,
            lineHeight: el.style.lineHeight,
            padding: el.style.padding,
            margin: el.style.margin,
            height: el.style.height,
          }));

          // Adjust styles for better PDF rendering
          elements.forEach((el) => {
            el.style.fontSize = "14px"; // Increased from 10px
            el.style.lineHeight = "1.4"; // Increased from 1

            // Make input fields more prominent
            if (
              el.tagName === "INPUT" ||
              el.classList.contains("MuiInputBase-input")
            ) {
              el.style.fontSize = "16px";
              el.style.padding = "8px";
              el.style.height = "auto";
              el.style.minHeight = "24px";
            }

            // Adjust label visibility
            if (el.classList.contains("MuiInputLabel-root")) {
              el.style.fontSize = "14px";
              el.style.transform = "none";
              el.style.position = "relative";
            }

            // Add more spacing between form fields
            if (el.classList.contains("MuiFormControl-root")) {
              el.style.marginBottom = "16px";
              el.style.marginTop = "8px";
            }
          });

          // Capture the section with adjusted styles
          const canvas = await html2canvas(section, {
            scale: 2,
            useCORS: true,
            logging: true,
            windowWidth: isMobile ? 1024 : undefined,
          });

          // Restore original styles
          originalStyles.forEach(
            ({ element, fontSize, lineHeight, padding, margin, height }) => {
              element.style.fontSize = fontSize;
              element.style.lineHeight = lineHeight;
              element.style.padding = padding;
              element.style.margin = margin;
              element.style.height = height;
            }
          );

          // Calculate dimensions
          const imgWidth = contentWidth;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          // Check if content needs new page
          if (currentY + imgHeight > pageHeight - 20) {
            doc.addPage();
            currentPage++;
            currentY = addHeaderToPage();
          }

          // Add section content
          doc.addImage(canvas, "PNG", margin, currentY, imgWidth, imgHeight);
          currentY += imgHeight + 10;
        } else {
          // Handle regular sections
          const canvas = await html2canvas(section, {
            scale: 2,
            useCORS: true,
            logging: true,
            windowWidth: isMobile ? 1024 : undefined,
          });

          // Verify canvas has content
          if (canvas.width === 0 || canvas.height === 0) {
            continue;
          }

          // Calculate dimensions
          const imgWidth = contentWidth;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          // Check if content needs new page
          if (currentY + imgHeight > pageHeight - 20) {
            doc.addPage();
            currentPage++;
            currentY = addHeaderToPage();
          }

          // Add section content
          doc.addImage(canvas, "PNG", margin, currentY, imgWidth, imgHeight);
          currentY += imgHeight + 10;
        }
      } catch (sectionError) {
        console.error(`Error processing section ${section.id}:`, sectionError);
      }

      // Restore hidden elements
      noPrintElements.forEach((el) => (el.style.display = ""));

      // Force new page after certain sections
      if (
        section.id.includes("pdf-4-total") ||
        section.id.includes("pdf-8-payoff")
      ) {
        doc.addPage();
        currentPage++;
        currentY = addHeaderToPage();
      }
    }

    // Add footers to all pages
    for (let i = 1; i <= currentPage; i++) {
      doc.setPage(i);
      addFooterToPage(i, currentPage);
    }

    // Save the PDF
    doc.save(`${calcType} calculator results.pdf`);

    // Cleanup progress indicator
    document.body.removeChild(progressContainer);

    // Show success snackbar
    setSnackbarData({
      message: "PDF generated successfully!",
      options: {
        variant: "success",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "top", horizontal: "center" },
      },
    });
  } catch (error) {
    // Cleanup progress indicator on error
    if (document.body.contains(progressContainer)) {
      document.body.removeChild(progressContainer);
    }

    console.error("Error generating PDF:", error);
    setSnackbarData({
      message: "Error generating PDF. Please try again.",
      options: {
        variant: "error",
        autoHideDuration: 5000,
        anchorOrigin: { vertical: "top", horizontal: "center" },
      },
    });
  }
};

export default handlePDFDownload;
